/**
 * The operator screen's keymap, in three layers (D32: operator actions are
 * the show style's):
 *
 *   built-in     `DEFAULT_BINDINGS` (`hotkeys.ts`): Take on F12/NumpadEnter/Enter/Space, …
 *   show style   its `keymap` (`@acp/engine/keymap.js`, from the production's
 *                snapshot), replacing an action's keys; and its adlib panels'
 *                item hotkeys
 *   user         the operator's own overrides, per user and show style, kept
 *                in THIS BROWSER (`userKeymapStorageKey`): an action's or an
 *                item's keys replaced, or unbound with an empty list
 *
 * Each layer replaces a command's whole key list; a command a layer does not
 * name keeps the layer below. `resolveKeymap` gives one entry per command
 * with its keys and where they came from; `hotkeyContextOf` turns entries
 * into what the key reducer reads; `keymapConflicts` finds a key bound to
 * two commands (the reducer would silently run only the first).
 *
 * **Which keys fire on a focused control** is not configured: it follows
 * from the key (`firesOnControls`). Enter, Space, a plain arrow or a plain
 * character belong to a focused button, select or field; F-keys,
 * NumpadEnter and modified keys do not, so they fire from anywhere.
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import { parseContract } from '@acp/api-contract/parsing.js';
import { hotkeyComboSchema, keymapSchema, MAX_KEYS_PER_ACTION, OPERATOR_ACTIONS, type Keymap, type OperatorAction } from '@acp/engine/keymap.js';
import { z } from 'zod';
import type { AdlibPanelView } from './adlib.js';
import { type Binding, type Combo, DEFAULT_BINDINGS, type HotkeyTarget, parseCombo } from './hotkeys.js';

export const ACTION_LABELS: Readonly<Record<OperatorAction, string>> = {
  take: 'Take',
  'next-forward': 'Next: one part on',
  'next-back': 'Next: one part back',
  'select-down': 'Cursor down',
  'select-up': 'Cursor up',
  'set-next': 'Set the cursor’s part next',
  'set-next-segment': 'Set the cursor’s segment next',
  'queue-segment': 'Queue the cursor’s segment',
};

/** The built-in keys per action (the table the reducer always had). */
function builtIn(action: OperatorAction): readonly string[] {
  return DEFAULT_BINDINGS.filter((b) => b.action === action).map((b) => b.combo);
}

export const BUILT_IN_KEYMAP: Readonly<Record<OperatorAction, readonly string[]>> = {
  take: builtIn('take'),
  'next-forward': builtIn('next-forward'),
  'next-back': builtIn('next-back'),
  'select-down': builtIn('select-down'),
  'select-up': builtIn('select-up'),
  'set-next': builtIn('set-next'),
  'set-next-segment': builtIn('set-next-segment'),
  'queue-segment': builtIn('queue-segment'),
};

// ─── combos ───

const MODIFIER_KEYS: ReadonlySet<string> = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'Fn', 'OS', 'Hyper', 'Super']);

/** A combo written canonically: `Ctrl+Alt+Shift+Meta+Key`, a letter upper-case, the space bar `Space`. */
export function formatCombo(combo: Combo): string {
  const key = combo.key === ' ' ? 'Space' : combo.key.length === 1 ? combo.key.toUpperCase() : combo.key;
  return [combo.ctrl ? 'Ctrl' : null, combo.alt ? 'Alt' : null, combo.shift ? 'Shift' : null, combo.meta ? 'Meta' : null, key].filter((part) => part !== null).join('+');
}

/** The canonical spelling of a written combo (`shift+n` → `Shift+N`), or null when it names no key. */
export function canonicalCombo(text: string): string | null {
  const combo = parseCombo(text);
  return combo === null ? null : formatCombo(combo);
}

/** The combo a key press records (the keymap editor's "press a key"); null for a modifier on its own. */
export function comboFromPress(press: { readonly key: string; readonly code: string; readonly shift: boolean; readonly ctrl: boolean; readonly alt: boolean; readonly meta: boolean }): string | null {
  if (MODIFIER_KEYS.has(press.key) || press.key === '' || press.key === 'Dead' || press.key === 'Unidentified') return null;
  const key = press.code === 'NumpadEnter' ? 'NumpadEnter' : press.key;
  return formatCombo({ key: key.length === 1 ? key.toLowerCase() : key, shift: press.shift, ctrl: press.ctrl, alt: press.alt, meta: press.meta });
}

const CONTROL_KEYS: ReadonlySet<string> = new Set(['Enter', ' ', 'Escape', 'Tab', 'Home', 'End', 'PageUp', 'PageDown', 'Backspace', 'Delete']);
const ARROWS: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * Whether the combo fires while focus is on a control (a button, a select, a
 * checkbox). Not when a focused control would use the key itself: Enter,
 * Space and the like, a plain arrow, a plain character. A Ctrl/Alt/Meta
 * combo, an F-key, NumpadEnter or a Shift+arrow always fires.
 */
export function firesOnControls(combo: Combo): boolean {
  if (combo.ctrl || combo.alt || combo.meta) return true;
  if (CONTROL_KEYS.has(combo.key)) return false;
  if (ARROWS.has(combo.key)) return combo.shift;
  return combo.key.length !== 1;
}

// ─── the layers ───

export type CommandSource = 'built-in' | 'show-style' | 'panel' | 'user';

/** An adlib panel item that can carry a hotkey. */
export type KeymapItem = { readonly panelId: string; readonly panelLabel: string; readonly index: number; readonly label: string; readonly hotkey: string | null };

/** A user's overrides: command id → its keys (empty: unbound). */
export type UserKeymap = Readonly<Record<string, readonly string[]>>;

export type KeymapEntry = {
  /** `action:<name>` or `item:<panelId>:<index>:<label>`. */
  readonly id: string;
  readonly label: string;
  /** What the editor groups it under: "Playout", or the item's panel. */
  readonly group: string;
  readonly combos: readonly string[];
  readonly source: CommandSource;
  /** The keys below the user's layer (what "reset" goes back to). */
  readonly inherited: readonly string[];
  readonly run: { readonly kind: 'action'; readonly action: OperatorAction } | { readonly kind: 'item'; readonly target: HotkeyTarget };
};

export function actionCommandId(action: OperatorAction): string {
  return `action:${action}`;
}

/**
 * A panel item's command: its panel, place AND label. Items have no id of their own, so a user's key saved for
 * "item 2" must not follow the place when the show style's panel changes under it — another item there (a label
 * that differs) is another command, and the saved key simply no longer applies.
 */
export function itemCommandId(panelId: string, index: number, label: string): string {
  return `item:${panelId}:${String(index)}:${label}`;
}

/** Canonical, deduplicated keys; a written combo that names no key is dropped. */
function canonicalList(combos: readonly string[]): string[] {
  const out: string[] = [];
  for (const combo of combos) {
    const canonical = canonicalCombo(combo);
    if (canonical !== null && !out.includes(canonical)) out.push(canonical);
  }
  return out;
}

function layered(id: string, inherited: readonly string[], inheritedSource: CommandSource, user: UserKeymap): { combos: string[]; source: CommandSource; inherited: string[] } {
  const own = Object.hasOwn(user, id) ? user[id] : undefined;
  const below = canonicalList(inherited);
  return own === undefined ? { combos: below, source: inheritedSource, inherited: below } : { combos: canonicalList(own), source: 'user', inherited: below };
}

/** The show style's keymap from a production's snapshot (D22/D32); null before D32, or when it has none or it does not parse. */
export function showStyleKeymapOf(snapshot: { readonly showStyle: Readonly<Record<string, JsonValue>> } | null): Keymap | null {
  if (snapshot === null || !Object.hasOwn(snapshot.showStyle, 'keymap')) return null;
  // Tolerant (D37): a newer SPA may have bound an action this build does not know — the rest still apply.
  const parsed = parseContract(keymapSchema, snapshot.showStyle['keymap'], 'tolerant');
  return parsed.success ? parsed.data : null;
}

/** The adlib panels' items as keymap commands. */
export function keymapItemsOf(panels: readonly AdlibPanelView[]): KeymapItem[] {
  return panels.flatMap((panel) => panel.items.map((item, index) => ({ panelId: panel.id, panelLabel: panel.label, index, label: item.label, hotkey: item.hotkey })));
}

/** Every command with its keys: the operator actions, then each panel item. */
export function resolveKeymap(showStyle: Keymap | null, items: readonly KeymapItem[], user: UserKeymap): KeymapEntry[] {
  const actions = OPERATOR_ACTIONS.map((action): KeymapEntry => {
    const fromShowStyle = showStyle?.[action];
    const id = actionCommandId(action);
    const layers = layered(id, fromShowStyle ?? BUILT_IN_KEYMAP[action], fromShowStyle === undefined ? 'built-in' : 'show-style', user);
    return { id, label: ACTION_LABELS[action], group: 'Playout', ...layers, run: { kind: 'action', action } };
  });
  const panelItems = items.map((item): KeymapEntry => {
    const id = itemCommandId(item.panelId, item.index, item.label);
    const layers = layered(id, item.hotkey === null ? [] : [item.hotkey], 'panel', user);
    return { id, label: item.label, group: item.panelLabel, ...layers, run: { kind: 'item', target: { panelId: item.panelId, index: item.index } } };
  });
  return [...actions, ...panelItems];
}

/** The key reducer's tables (`HotkeyContext.bindings` and `.items`) for the resolved entries. */
export function hotkeyContextOf(entries: readonly KeymapEntry[]): { readonly bindings: readonly Binding[]; readonly items: readonly { readonly hotkey: string; readonly target: HotkeyTarget }[] } {
  const bindings: Binding[] = [];
  const items: { hotkey: string; target: HotkeyTarget }[] = [];
  for (const entry of entries) {
    for (const combo of entry.combos) {
      const parsed = parseCombo(combo);
      if (parsed === null) continue;
      if (entry.run.kind === 'action') bindings.push({ combo, action: entry.run.action, anyFocus: firesOnControls(parsed) });
      else items.push({ hotkey: combo, target: entry.run.target });
    }
  }
  return { bindings, items };
}

/** The keys an action runs on (the TAKE button's hint shows the ones that fire from anywhere). */
export function keysFor(entries: readonly KeymapEntry[], action: OperatorAction, anyFocusOnly: boolean): string[] {
  const entry = entries.find((e) => e.run.kind === 'action' && e.run.action === action);
  return (entry?.combos ?? []).filter((combo) => {
    const parsed = parseCombo(combo);
    return parsed !== null && (!anyFocusOnly || firesOnControls(parsed));
  });
}

export type KeymapConflict = { readonly combo: string; readonly ids: readonly string[] };

/** Every key bound to more than one command, in the order the keys first appear. */
export function keymapConflicts(entries: readonly KeymapEntry[]): KeymapConflict[] {
  const byCombo = new Map<string, string[]>();
  for (const entry of entries) {
    for (const combo of entry.combos) {
      const ids = byCombo.get(combo) ?? [];
      if (!ids.includes(entry.id)) ids.push(entry.id);
      byCombo.set(combo, ids);
    }
  }
  return [...byCombo.entries()].filter(([, ids]) => ids.length > 1).map(([combo, ids]) => ({ combo, ids }));
}

/** The user layer with one command's keys replaced (`null`: back to what it inherits). */
export function withOverride(user: UserKeymap, id: string, combos: readonly string[] | null): UserKeymap {
  const next: Record<string, readonly string[]> = { ...user };
  if (combos === null) delete next[id];
  else next[id] = canonicalList(combos).slice(0, MAX_KEYS_PER_ACTION);
  return next;
}

// ─── the user layer's storage (this browser) ───

const userKeymapSchema = z.record(z.string().max(600), z.array(hotkeyComboSchema).max(MAX_KEYS_PER_ACTION));

/** Where a user's overrides for a show style live in this browser (null show style: a production from before D32). */
export function userKeymapStorageKey(userId: string, showStyleId: string | null): string {
  return `acp.keymap.v1:${userId}:${showStyleId ?? '-'}`;
}

/** The stored overrides, or none when absent, unreadable or not a keymap (storage is a convenience, never trusted). */
export function parseUserKeymap(raw: string | null): UserKeymap {
  if (raw === null) return {};
  try {
    const parsed = userKeymapSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}
